import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

const CreditsContext = createContext(null);

// Credit costs mapping - synced with backend config.py
const CREDIT_COSTS = {
  // Text Generation (Core)
  generate_copy: 5,
  batch_generate: 5,
  copy_variants: 18,
  copy_single: 4,
  analyze_web: 7,
  generate_brief: 12,
  generate_landing: 14,
  landing_regenerate: 10,
  generate_email: 10,
  email_sequence: 30,
  email_single: 10,
  generate_whatsapp: 5,
  strategy_suggest: 5,
  brief_analyze: 7,
  
  // Image Generation
  image_nano_banana: 6,
  image_freepik: 8,
  image_flux_pro: 11,
  image_flux_pro_fal: 11,
  image_flux_schnell: 6,
  image_static: 6,
  image_carousel: 18,
  image_carousel_fal: 20,
  image_pack: 18,
  image_compose_only: 3,
  
  // Video Generation
  video_veo2: 18,
  video_kling: 22,
  video_kling_fal: 22,
  video_kling_turbo: 15,
  video_heygen: 54,
  video_broll: 18,
  video_avatar: 54,
  video_ugc: 22,
  
  // Audio/Voice
  voice_elevenlabs: 10,
  voice_gemini_tts: 5,
  voice_preview: 0,
  voice_tts_short: 5,
  voice_tts_long: 10,
  
  // Video Composition
  process_creatomate: 12,
  
  // Campaign Guides
  generate_meta_guide: 8,
  generate_google_guide: 8,
  generate_ghl_guide: 10,
  generate_ads_design_brief: 6,
  
  // Integration Exports
  export_ghl: 15,
  export_wordpress: 10,
  export_esp_emails: 12,
  deploy_brevo: 10,
  deploy_activecampaign: 12,
  deploy_wordpress: 10,
};

// User-friendly names for operations
const OPERATION_NAMES = {
  generate_copy: 'Generar copy',
  generate_landing: 'Generar landing',
  landing_regenerate: 'Regenerar landing',
  generate_email: 'Generar email',
  email_sequence: 'Secuencia de emails',
  copy_variants: 'Variantes de copy',
  image_nano_banana: 'Generar imagen (Gemini)',
  image_flux_pro: 'Generar imagen (Flux Pro)',
  image_flux_schnell: 'Generar imagen (Flux Rápido)',
  video_kling: 'Generar vídeo',
  video_avatar: 'Avatar con voz',
  voice_elevenlabs: 'Voz IA (ElevenLabs)',
  voice_gemini_tts: 'Voz IA (Gemini)',
  process_creatomate: 'Procesar vídeo',
  deploy_wordpress: 'Publicar en WordPress',
  deploy_brevo: 'Enviar por Brevo',
};

export function CreditsProvider({ children }) {
  const { user, isAuthenticated, api } = useAuth();
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Use ref for api to prevent useCallback/useEffect re-runs when api reference changes
  const apiRef = useRef(api);
  apiRef.current = api;

  // Fetch balance from server - stable callback that doesn't change with api
  const fetchBalance = useCallback(async () => {
    if (!isAuthenticated || !apiRef.current) {
      setBalance(0);
      setLoading(false);
      return;
    }
    
    try {
      const response = await apiRef.current.get('/billing/status');
      const newBalance = response.data?.credits_balance || 0;
      setBalance(newBalance);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching credit balance:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]); // Only depends on isAuthenticated, not api

  // Initial fetch - only runs when user changes or isAuthenticated changes
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance, user?.id]);

  // Refresh balance (call after any credit-consuming action)
  const refreshBalance = useCallback(async () => {
    await fetchBalance();
  }, [fetchBalance]);

  // Get cost for an operation
  const getCost = useCallback((operation, quantity = 1) => {
    const baseCost = CREDIT_COSTS[operation] || 0;
    return baseCost * quantity;
  }, []);

  // Check if user has enough credits
  const hasEnoughCredits = useCallback((operation, quantity = 1) => {
    const cost = getCost(operation, quantity);
    return balance >= cost;
  }, [balance, getCost]);

  // Get operation name in Spanish
  const getOperationName = useCallback((operation) => {
    return OPERATION_NAMES[operation] || operation;
  }, []);

  const value = {
    balance,
    loading,
    lastUpdated,
    refreshBalance,
    getCost,
    hasEnoughCredits,
    getOperationName,
    CREDIT_COSTS,
  };

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits() {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  return context;
}

export default CreditsContext;
